"use client";

/**
 * SollIstCheck.tsx — Soll-Ist-Abgleich (finance W2, package p1-payslip-sollist).
 *
 * THE flagship of the payslip screen: compares what the payslip ACTUALLY shows
 * (IST — parsed by the backend) with what the verified 2026 net engine EXPECTS
 * (SOLL — computeNetto from @/lib/finance-engine) for the same gross and the
 * user-confirmed parameters (Steuerklasse, KV-Zusatzbeitrag, Bundesland, Kinder).
 *
 * Convention: payslip brutto/netto are MONTHLY → engine gets brutto×12, engine
 * results are already monthly (annual/12, rounded). A line is flagged when the
 * delta exceeds the tolerance max(5 €, 2 %) — deliberately not tighter, because
 * a real monthly payslip legitimately differs from a simplified annual/12
 * calculation (Lohnsteuertabelle, Einmalzahlungen, Rundung). The caveat line
 * below the table says exactly that.
 *
 * Events: payslip_sollist_view (once per result) ·
 *         payslip_sollist_flag { n_flags, n_missing } (on every count change).
 */

import { useEffect, useMemo, useRef, useState, type CSSProperties } from "react";
import { AlertTriangle, CheckCircle, Info } from "lucide-react";
import { T, useTextLocale, tl } from "@/components/t";
import { SectionHeader, Tint } from "@/components/finance/Finance";
import { Card, Icon, Stack } from "@/design";
import { track } from "@/lib/analytics";
import { computeNetto, setTaxYear, YEARS, type NettoOpts } from "@/lib/finance-engine";
import { roman } from "@/lib/netto";

/* ── W3: Beschäftigungsart (employment type) modes ──────────────────────────
 * W2 assumed a normal GKV employee. Payslips also come from Minijobs, Werk-
 * studenten and privately-insured (PKV) employees — each has a fundamentally
 * different SOLL. Mode is auto-detected from the parse (best-effort) and stays
 * user-overridable via the segmented control. The engine already supports the
 * math (svMode 'werk'/'minijob', opts.pkv); we only pick the branch + which
 * rows are meaningful. */
type Mode = "normal" | "minijob" | "werk" | "pkv";

const MINIJOB_MAX = 603; // Minijob-Grenze 2026 (verified)
/* Monthly-brutto floor above which PKV instead of GKV is even possible (JAEG
 * 77.400 €/Jahr → 6.450 €/Monat). Used only as a fallback PKV signal. */
const JAEG_MO = 6450;

/** Does any parsed deduction match this class (amount > 0)? */
function hasDed(result: SollIstResult | undefined, re: RegExp): boolean {
  for (const d of result?.deductions || []) {
    const l = d?.label || "";
    if (l && re.test(l) && typeof d?.amount === "number" && d.amount > 0) return true;
  }
  return false;
}

/** Private-KV line on the payslip (PKV premium / employer subsidy), if present. */
function privateKvAmount(result: SollIstResult | undefined): number {
  for (const d of result?.deductions || []) {
    const l = d?.label || "";
    if (l && /privat|\bpkv\b|private kranken/i.test(l) && typeof d?.amount === "number" && d.amount > 0) {
      return d.amount;
    }
  }
  return 0;
}

/* Best-effort mode auto-detect from the parsed payslip. Order matters:
 * Minijob (tiny brutto, no wage tax) → PKV (private KV line, or high brutto w/o
 * GKV-KV) → Werkstudent (RV present but KV+AV absent) → else normal. */
function detectMode(result: SollIstResult | undefined): Mode {
  const brutto = Number(result?.summary?.brutto) || 0;
  if (brutto <= 0) return "normal";
  const hasLst = hasDed(result, /lohnsteuer|\blst\b/i);
  const hasKv = hasDed(result, /krankenvers|krankenkasse|\bkv\b/i);
  const hasRv = hasDed(result, /rentenvers|\brv\b/i);
  const hasAv = hasDed(result, /arbeitslosenvers|\bav\b/i);
  // Minijob: at/under the 603 € threshold and no wage tax withheld.
  if (brutto <= MINIJOB_MAX && !hasLst) return "minijob";
  // PKV: an explicit private-KV line, OR a real employee (RV present) above the
  // JAEG with no statutory KV line recognised.
  if (privateKvAmount(result) > 0) return "pkv";
  if (hasRv && !hasKv && brutto >= JAEG_MO) return "pkv";
  // Werkstudent: RV-only pattern — pays RV but is exempt from KV/PV/AV.
  if (hasRv && !hasKv && !hasAv) return "werk";
  return "normal";
}

/** Which comparison rows are meaningful for the mode (others are suppressed). */
function visibleKeysFor(mode: Mode): Set<RowKey> {
  if (mode === "werk") return new Set<RowKey>(["lohnsteuer", "rv", "netto"]);
  if (mode === "pkv") return new Set<RowKey>(["lohnsteuer", "rv", "av", "netto"]);
  return new Set<RowKey>(["lohnsteuer", "kv", "rv", "av", "pv", "netto"]);
}

const MODE_OPTS: { key: Mode; de: string; en: string }[] = [
  { key: "normal", de: "Angestellt (GKV)", en: "Employee (GKV)" },
  { key: "werk", de: "Werkstudent:in", en: "Working student" },
  { key: "minijob", de: "Minijob", en: "Minijob" },
  { key: "pkv", de: "Privat (PKV)", en: "Private (PKV)" },
];

/* Minijob: a Minijobber should have NO tax/SV lines — a present deduction is the
 * anomaly. Overrides the GKV-centric flagCopy for that case only. */
function minijobFlagCopy(key: RowKey): { causeDe: string; causeEn: string; actDe: string; actEn: string } {
  if (key === "netto") {
    return {
      causeDe:
        "Bei einem Minijob wird normalerweise das volle Brutto ausgezahlt. Ist dein Netto niedriger, wurde etwas abgezogen — z. B. der pauschale RV-Eigenanteil (3,6 %, wenn du dich nicht befreit hast) oder eine private Abzugsposition.",
      causeEn:
        "A minijob is normally paid out in full. A lower net means something was deducted — e.g. the flat pension employee share (3.6% if you did not opt out) or a private deduction line.",
      actDe:
        "Prüfe auf der Abrechnung, ob ein RV-Eigenanteil ausgewiesen ist. Willst du ihn nicht zahlen, kannst du dich einmalig von der Rentenversicherungspflicht befreien lassen (Antrag beim Arbeitgeber).",
      actEn:
        "Check whether a pension employee share is listed. If you do not want it, you can file a one-off exemption from pension insurance (via your employer).",
    };
  }
  return {
    causeDe:
      "Diese Position gehört normalerweise nicht auf eine Minijob-Abrechnung: Bis 603 € pauschaliert der Arbeitgeber die Abgaben, du selbst zahlst keine Lohnsteuer und keine Sozialabgaben (außer ggf. den RV-Eigenanteil). Taucht hier ein Abzug auf, ist die Beschäftigung womöglich gar kein Minijob — oder oben ist die falsche Beschäftigungsart gewählt.",
    causeEn:
      "This line normally does not belong on a minijob payslip: the employer pays flat-rate contributions, you owe no wage tax and no social contributions (except possibly the pension employee share). A deduction here means the job may not be a minijob — or the wrong employment type is selected above.",
    actDe:
      "Prüfe oben die Beschäftigungsart. Ist es wirklich ein Minijob, kläre die Position mit der Personalabteilung — evtl. wurde die Abrechnung falsch als sozialversicherungspflichtig geführt.",
    actEn:
      "Check the employment type above. If it really is a minijob, clarify the line with payroll — the payslip may have been processed as a regular insurable job by mistake.",
  };
}

import styles from "./FinanzenPayslipV2.module.css";

/* Structural subset of the parent's PayslipResult (interface is not exported
 * there — structural typing keeps `<SollIstCheck result={result} />` valid). */
interface SollIstDeduction {
  label: string;
  amount: number;
  category: string;
}
export interface SollIstResult {
  summary: {
    brutto: number;
    netto: number;
    month?: string;
    tax_class?: string;
    kv_zusatz?: number;
    bundesland?: string;
  };
  deductions: SollIstDeduction[];
  is_example?: boolean;
}

type DedKey = "lohnsteuer" | "kv" | "rv" | "av" | "pv";
type RowKey = DedKey | "netto";
type RowStatus = "ok" | "flag" | "missing";

interface CmpRow {
  key: RowKey;
  de: string;
  en: string;
  soll: number;
  ist: number | null;
  delta: number | null;
  pct: number | null;
  status: RowStatus;
}

const ROW_DEFS: { key: RowKey; de: string; en: string }[] = [
  { key: "lohnsteuer", de: "Lohnsteuer", en: "Wage tax" },
  { key: "kv", de: "Krankenversicherung", en: "Health insurance" },
  { key: "rv", de: "Rentenversicherung", en: "Pension insurance" },
  { key: "av", de: "Arbeitslosenversicherung", en: "Unemployment insurance" },
  { key: "pv", de: "Pflegeversicherung", en: "Care insurance" },
  { key: "netto", de: "Netto / Monat", en: "Net / month" },
];

/* Map parsed deduction lines onto the five classes by label heuristics
 * (backend labels follow payslip.py's contract, but abbreviations happen). */
const MATCHERS: { key: DedKey; re: RegExp }[] = [
  { key: "lohnsteuer", re: /lohnsteuer|\blst\b/i },
  { key: "kv", re: /krankenvers|krankenkasse|\bkv\b/i },
  { key: "rv", re: /rentenvers|\brv\b/i },
  { key: "av", re: /arbeitslosenvers|\bav\b/i },
  { key: "pv", re: /pflegevers|\bpv\b/i },
];
/* Soli & Kirchensteuer are handled inside the engine's Netto, not as own rows. */
const SKIP_RE = /kirch|solidar|\bsoli\b/i;

const BL_OPTS: { code: string; name: string }[] = [
  { code: "BW", name: "Baden-Württemberg" },
  { code: "BY", name: "Bayern" },
  { code: "BE", name: "Berlin" },
  { code: "BB", name: "Brandenburg" },
  { code: "HB", name: "Bremen" },
  { code: "HH", name: "Hamburg" },
  { code: "HE", name: "Hessen" },
  { code: "MV", name: "Mecklenburg-Vorpommern" },
  { code: "NI", name: "Niedersachsen" },
  { code: "NW", name: "Nordrhein-Westfalen" },
  { code: "RP", name: "Rheinland-Pfalz" },
  { code: "SL", name: "Saarland" },
  { code: "SN", name: "Sachsen" },
  { code: "ST", name: "Sachsen-Anhalt" },
  { code: "SH", name: "Schleswig-Holstein" },
  { code: "TH", name: "Thüringen" },
];

function normName(s: string): string {
  return s
    .trim()
    .toLowerCase()
    .replace(/ü/g, "u")
    .replace(/ä/g, "a")
    .replace(/ö/g, "o")
    .replace(/ß/g, "ss")
    .replace(/\s+/g, "-");
}

/** Parser returns full names ("Bayern"); map to the 2-letter code, default null. */
function blFromName(name?: string): string | null {
  if (!name) return null;
  const raw = String(name).trim();
  if (/^[A-Za-z]{2}$/.test(raw)) {
    const up = raw.toUpperCase();
    if (BL_OPTS.some((b) => b.code === up)) return up;
  }
  const n = normName(raw);
  if (n === "nrw") return "NW";
  const hit = BL_OPTS.find((b) => normName(b.name) === n);
  return hit ? hit.code : null;
}

/** Roman ("I"…"VI", also "IV/IV", "Klasse 3") → 1..6; defaults to 1. */
function parseTaxClass(s?: string): number {
  if (!s) return 1;
  const t = String(s).toUpperCase();
  const rom = t.replace(/[^IVX]/g, "");
  const seq: [string, number][] = [["VI", 6], ["IV", 4], ["III", 3], ["II", 2], ["I", 1], ["V", 5]];
  for (const [k, v] of seq) if (rom === k) return v;
  for (const [k, v] of seq) if (rom.startsWith(k)) return v;
  const d = t.match(/[1-6]/);
  if (d) return parseInt(d[0], 10);
  return 1;
}

/** kv_zusatz arrives as percent (e.g. 1.7); guard range, accept fraction form,
 *  fall back to the official 2026 average (2,9 %). */
function guardKvz(z?: number): number {
  const def = YEARS[2026].kvz * 100;
  if (typeof z !== "number" || !isFinite(z)) return def;
  if (z >= 1 && z <= 6) return Math.round(z * 100) / 100;
  if (z > 0 && z < 0.07) return Math.round(z * 10000) / 100; // 0.029 → 2.9
  return def;
}

const eur0 = (n: number) => `${Math.round(n).toLocaleString("de-DE")} €`;
const eur2 = (n: number) =>
  `${n.toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;

/* One plain-German likely cause + one action hint per flagged class. */
function flagCopy(
  key: RowKey,
  higher: boolean,
  ctx: { kvz: number; bl: string },
): { causeDe: string; causeEn: string; actDe: string; actEn: string } {
  const kvzTxt = ctx.kvz.toLocaleString("de-DE");
  switch (key) {
    case "lohnsteuer":
      return higher
        ? {
            causeDe:
              "Du zahlst mehr Lohnsteuer, als deine Angaben erwarten lassen. Wahrscheinliche Ursachen: falsche oder ungünstige Steuerklasse (z. B. V/VI statt III/IV), fehlende Kinderfreibeträge (wirken vor allem auf Soli und Kirchensteuer) — oder eine Einmalzahlung in diesem Monat.",
            causeEn:
              "You pay more wage tax than your inputs suggest. Likely causes: a wrong or unfavourable tax class (e.g. V/VI instead of III/IV), missing child allowances (they mainly affect solidarity surcharge and church tax) — or a one-off payment this month.",
            actDe:
              "Prüfe Steuerklasse und Kinderfreibeträge in deinen ELStAM (über elster.de oder dein Finanzamt) — eine Korrektur übernimmt dein Arbeitgeber im Folgemonat automatisch.",
            actEn:
              "Check tax class and child allowances in your ELStAM data (elster.de or your tax office) — your employer applies corrections automatically the following month.",
          }
        : {
            causeDe:
              "Du zahlst weniger Lohnsteuer als erwartet. Meist ist ein Lohnsteuer-Freibetrag aktiv (z. B. Fahrtkosten oder doppelte Haushaltsführung) — dann ist das korrekt so. Oder deine echte Steuerklasse ist günstiger als oben eingestellt.",
            causeEn:
              "You pay less wage tax than expected. Usually a wage-tax allowance (Freibetrag) is active (e.g. commuting costs) — then this is correct. Or your real tax class is more favourable than set above.",
            actDe:
              "Keinen Freibetrag beantragt? Dann Steuerklasse oben prüfen — bleibt die Differenz, kläre sie mit der Personalabteilung.",
            actEn:
              "No allowance on file? Then check the tax class above — if the gap remains, clarify it with payroll.",
          };
    case "kv":
      return higher
        ? {
            causeDe: `Dein KV-Beitrag ist höher als erwartet — vermutlich verlangt deine Kasse einen höheren Zusatzbeitrag als die angesetzten ${kvzTxt} % (Kassen-Spanne 2026: ca. 2,2 bis 4,4 %).`,
            causeEn: `Your health-insurance line is higher than expected — your Kasse probably charges a higher additional rate than the assumed ${kvzTxt}% (2026 range: roughly 2.2–4.4%).`,
            actDe:
              "Trag oben den Zusatzbeitrag deiner Kasse ein (steht auf der Abrechnung). Zahlst du deutlich mehr als der Schnitt von 2,9 %, spart ein Kassenwechsel dauerhaft Geld.",
            actEn:
              "Enter your Kasse's real additional rate above (it is printed on the payslip). If you pay well above the 2.9% average, switching Kasse saves money permanently.",
          }
        : {
            causeDe: `Dein KV-Beitrag ist niedriger als erwartet — deine Kasse ist günstiger als die angesetzten ${kvzTxt} %, oder es gilt der ermäßigte Satz (14,0 %, ohne Krankengeld-Anspruch).`,
            causeEn: `Your health-insurance line is lower than expected — a cheaper Kasse than the assumed ${kvzTxt}%, or the reduced 14.0% rate (no sickness benefit) applies.`,
            actDe: "Zusatzbeitrag oben anpassen — passt es dann, ist alles in Ordnung.",
            actEn: "Adjust the additional rate above — if it then matches, everything is fine.",
          };
    case "pv":
      return higher
        ? {
            causeDe:
              ctx.bl === "SN"
                ? "Dein PV-Beitrag ist höher als erwartet. In Sachsen tragen Beschäftigte 0,5 Punkte mehr Pflegeversicherung als hier angesetzt — dort ist ein höherer Ist-Wert normal. Zusätzlich möglich: Kinderlosen-Zuschlag (+0,6 Punkte) trotz Kindern."
                : "Dein PV-Beitrag ist höher als erwartet. Typisch: Der Kinderlosen-Zuschlag (+0,6 Punkte) wird berechnet, obwohl du Kinder hast — oder du arbeitest in Sachsen (dort tragen Beschäftigte 0,5 Punkte mehr).",
            causeEn:
              ctx.bl === "SN"
                ? "Your care-insurance line is higher than expected. In Saxony employees pay 0.5 points more than assumed here — a higher actual value is normal there. Also possible: the childless surcharge (+0.6 points) despite kids."
                : "Your care-insurance line is higher than expected. Typical: the childless surcharge (+0.6 points) is applied although you have kids — or you work in Saxony (employees pay 0.5 points more there).",
            actDe:
              "Kinderzahl oben prüfen und die Elterneigenschaft ggf. beim Arbeitgeber nachweisen (Geburtsurkunde reicht) — zu viel gezahlte Zuschläge gibt es rückwirkend zurück.",
            actEn:
              "Check the number of kids above and prove parenthood to your employer if needed — overpaid surcharges are refunded retroactively.",
          }
        : {
            causeDe:
              "Dein PV-Beitrag ist niedriger als erwartet — je Kind zwischen 2 und 5 sinkt der Beitrag um 0,25 Punkte. Wahrscheinlich stimmt die Kinderzahl oben nicht.",
            causeEn:
              "Your care-insurance line is lower than expected — each child aged 2–5 lowers the rate by 0.25 points. The number of kids above is probably off.",
            actDe: "Kinderzahl oben anpassen — passt es dann, ist alles korrekt.",
            actEn: "Adjust the number of kids above — if it then matches, all good.",
          };
    case "rv":
    case "av":
      return {
        causeDe:
          "RV (18,6 %) und AV (2,6 %) sind Festsätze, je zur Hälfte von dir und deinem Arbeitgeber, bis zur Beitragsbemessungsgrenze. Abweichungen entstehen meist durch bAV-Entgeltumwandlung (senkt das SV-Brutto), den Midijob-Übergangsbereich oder Einmalzahlungen.",
        causeEn:
          "Pension (18.6%) and unemployment insurance (2.6%) are fixed rates, split with your employer, up to the contribution ceiling. Gaps usually come from company-pension deferral (lowers the SV gross), the midijob transition band or one-off payments.",
        actDe:
          "Vergleiche auf der Abrechnung das SV-Brutto mit dem Gesamtbrutto — weichen die beiden ab, ist das meist schon die Erklärung.",
        actEn:
          "Compare the 'SV-Brutto' with the total gross on the payslip — if the two differ, that usually explains it.",
      };
    default: // netto
      return higher
        ? {
            causeDe:
              "Dein ausgezahltes Netto ist höher als erwartet — z. B. durch steuerfreie Zuschläge (Nacht-/Sonntagsarbeit), Spesen oder einen aktiven Freibetrag.",
            causeEn:
              "Your paid-out net is higher than expected — e.g. tax-free supplements (night/Sunday work), expenses, or an active allowance.",
            actDe:
              "Prüfe die Zuschlags-Positionen auf der Abrechnung — steuerfreie Zulagen erhöhen dein Netto völlig legal.",
            actEn:
              "Check the supplement lines on the payslip — tax-free supplements raise your net perfectly legally.",
          }
        : {
            causeDe:
              "Dein ausgezahltes Netto ist niedriger als erwartet. Häufig stecken Netto-Abzüge dahinter (bAV, JobRad, Sachbezüge, Vorschüsse) — oder eine der anderen markierten Positionen.",
            causeEn:
              "Your paid-out net is lower than expected. Usually post-tax deductions are the reason (company pension, JobRad, benefits in kind, advances) — or one of the other flagged lines.",
            actDe:
              "Schau auf der Abrechnung zwischen 'Netto' und 'Auszahlungsbetrag' — dort stehen die Abzüge nach Steuern.",
            actEn:
              "Look between 'Netto' and 'Auszahlungsbetrag' on the payslip — post-tax deductions are listed there.",
          };
  }
}

/* Inline styles with design tokens — this screen's module css has no field/table classes. */
const paramsGrid: CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
  gap: "var(--ck-space-3)",
};
const fieldLabel: CSSProperties = {
  display: "block",
  fontSize: "var(--ck-text-xs)",
  fontWeight: 600,
  color: "var(--ck-ink-muted)",
  marginBottom: 4,
};
const fieldCtl: CSSProperties = {
  width: "100%",
  padding: "8px 10px",
  border: "1px solid var(--ck-hairline)",
  borderRadius: "var(--ck-radius-sm)",
  fontFamily: "var(--ck-font-sans)",
  fontSize: "var(--ck-text-sm)",
  color: "var(--ck-ink)",
  background: "transparent",
};
const colHead: CSSProperties = {
  fontSize: "var(--ck-text-xs)",
  fontWeight: 700,
  color: "var(--ck-ink-muted)",
  textTransform: "uppercase",
  letterSpacing: "0.04em",
};
const rowGrid: CSSProperties = {
  display: "grid",
  gridTemplateColumns: "minmax(0, 1.4fr) 1fr 1.1fr 28px",
  gap: "var(--ck-space-3)",
  alignItems: "center",
  padding: "8px 0",
};
const cellNum: CSSProperties = {
  textAlign: "right",
  fontVariantNumeric: "tabular-nums",
  whiteSpace: "nowrap",
  fontSize: "var(--ck-text-sm)",
  color: "var(--ck-ink)",
};
const cellLabel: CSSProperties = {
  fontSize: "var(--ck-text-sm)",
  fontWeight: 600,
  color: "var(--ck-ink)",
  minWidth: 0,
};

export default function SollIstCheck({ result }: { result: SollIstResult }) {
  const loc = useTextLocale();

  const monthlyBrutto = Number(result?.summary?.brutto) || 0;
  const monthlyNetto = Number(result?.summary?.netto) || 0;
  // Range-guard: payslip brutto/netto are monthly; outside this window the
  // engine comparison (svMode "normal", GKV) would only produce noise.
  // W3: floor + netto rule depend on the auto-detected employment type — a
  // Minijob sits below the 650 € floor and pays out the full brutto (netto ==
  // brutto), a Werkstudent can earn under 650 €. detectMode is a pure function
  // of props, so it is safe to read here (before the mode state exists).
  const detected = detectMode(result);
  const minBrutto = detected === "minijob" ? 100 : detected === "werk" ? 400 : 650;
  const nettoOk =
    detected === "minijob" ? monthlyNetto <= monthlyBrutto : monthlyNetto < monthlyBrutto;
  const valid =
    monthlyBrutto >= minBrutto &&
    monthlyBrutto <= 60000 &&
    monthlyNetto > 0 &&
    nettoOk;

  // User-confirmable params, prefilled from the parsed payslip (deterministic
  // initializers from props — no storage/URL reads, hydration-safe).
  const [sk, setSk] = useState<number>(() => parseTaxClass(result?.summary?.tax_class));
  const [kvz, setKvz] = useState<number>(() => guardKvz(result?.summary?.kv_zusatz));
  const [bl, setBl] = useState<string>(() => blFromName(result?.summary?.bundesland) || "BY");
  const [kids, setKids] = useState<number>(0);
  // W3: Beschäftigungsart — auto-detected, user-overridable.
  const [mode, setMode] = useState<Mode>(() => detectMode(result));
  const autoRef = useRef(true); // true until the user overrides the mode

  // Re-sync the params if a different payslip result arrives on the same mount.
  const lastResult = useRef(result);
  useEffect(() => {
    if (lastResult.current === result) return;
    lastResult.current = result;
    setSk(parseTaxClass(result?.summary?.tax_class));
    setKvz(guardKvz(result?.summary?.kv_zusatz));
    setBl(blFromName(result?.summary?.bundesland) || "BY");
    setKids(0);
    autoRef.current = true;
    setMode(detectMode(result));
  }, [result]);

  // Kirchensteuer on the payslip? Then the SOLL-Netto must include it too.
  const kircheIst = useMemo(() => {
    const hit = (result?.deductions || []).find(
      (d) => /kirch/i.test(d?.label || "") && typeof d?.amount === "number" && d.amount > 0,
    );
    return hit ? hit.amount : 0;
  }, [result]);

  // IST per class from the parsed deductions (sums split lines, first match wins).
  const istDed = useMemo(() => {
    const acc: Record<DedKey, number | null> = { lohnsteuer: null, kv: null, rv: null, av: null, pv: null };
    for (const d of result?.deductions || []) {
      const label = d?.label || "";
      if (!label || SKIP_RE.test(label)) continue;
      if (typeof d.amount !== "number" || !isFinite(d.amount)) continue;
      for (const m of MATCHERS) {
        if (m.re.test(label)) {
          acc[m.key] = (acc[m.key] ?? 0) + d.amount;
          break;
        }
      }
    }
    return acc;
  }, [result]);

  const cmp = useMemo(() => {
    // Pin the engine to 2026 params — another tool (e.g. /netto's year toggle)
    // may have switched the mutable parameter set to 2025.
    setTaxYear(2026);
    const churchPct = bl === "BY" || bl === "BW" ? 8 : 9;
    const opts: NettoOpts = {
      steuerklasse: sk,
      kinderlos: kids === 0,
      kinderAnzahl: kids,
      kvZusatz: kvz / 100,
      kirche: kircheIst > 0,
      churchRate: churchPct / 100,
      // W3: werk → RV-only engine branch; pkv → KV/PV = 0. Minijob is handled
      // below without the engine (SOLL netto = brutto).
      svMode: mode === "werk" ? "werk" : "normal",
      pkv: mode === "pkv",
    };
    let soll: Record<RowKey, number>;
    if (mode === "minijob") {
      // A Minijob is paid out in full: SOLL netto = brutto, no tax/SV deductions.
      soll = { lohnsteuer: 0, kv: 0, rv: 0, av: 0, pv: 0, netto: monthlyBrutto };
    } else {
      const r = computeNetto(Math.round(monthlyBrutto * 12), opts);
      soll = {
        lohnsteuer: r.est,
        kv: r.kv,
        rv: r.rv,
        av: r.av,
        pv: r.pv,
        netto: r.net,
      };
    }
    // Which rows are meaningful for the mode. For Minijob we additionally surface
    // any deduction the payslip DOES contain, so an unexpected line gets flagged.
    const visibleKeys = visibleKeysFor(mode);
    if (mode === "minijob") {
      for (const m of MATCHERS) if ((istDed[m.key] ?? null) !== null) visibleKeys.add(m.key);
    }
    const visibleRowDefs = ROW_DEFS.filter((d) => visibleKeys.has(d.key));
    // PKV: the private premium is the user's own line — informational, never a flag.
    const pkvPremium = mode === "pkv" ? privateKvAmount(result) : 0;
    const istMap: Record<RowKey, number | null> = { ...istDed, netto: monthlyNetto };
    const rows: CmpRow[] = [];
    let nFlags = 0;
    let nMissing = 0;
    for (const def of visibleRowDefs) {
      const s = soll[def.key];
      const i = istMap[def.key];
      if (i === null || i === undefined) {
        // Not on the payslip (or not recognised). Only worth a hint if the
        // engine expects a real amount; never counted as a hard flag.
        if (s >= 5) {
          rows.push({ ...def, soll: s, ist: null, delta: null, pct: null, status: "missing" });
          nMissing += 1;
        }
        continue;
      }
      const delta = i - s;
      const tol = Math.max(5, 0.02 * Math.max(s, i));
      const flagged = Math.abs(delta) > tol;
      if (flagged) nFlags += 1;
      rows.push({
        ...def,
        soll: s,
        ist: i,
        delta,
        pct: s > 0 ? Math.round((Math.abs(delta) / s) * 100) : null,
        status: flagged ? "flag" : "ok",
      });
    }
    return { rows, nFlags, nMissing, churchPct, pkvPremium };
  }, [monthlyBrutto, monthlyNetto, istDed, kircheIst, sk, kvz, bl, kids, mode, result]);

  // View once per mount (component remounts per new result).
  const viewed = useRef(false);
  useEffect(() => {
    if (!valid || viewed.current) return;
    viewed.current = true;
    track("payslip_sollist_view", { is_example: !!result?.is_example, mode });
  }, [valid, result, mode]);

  // Flag count — fires on change only (incl. the clean 0-flag case → match rate).
  const lastSig = useRef<string | null>(null);
  useEffect(() => {
    if (!valid) return;
    const sig = `${cmp.nFlags}/${cmp.nMissing}`;
    if (lastSig.current === sig) return;
    lastSig.current = sig;
    track("payslip_sollist_flag", {
      n_flags: cmp.nFlags,
      n_missing: cmp.nMissing,
      is_example: !!result?.is_example,
      mode,
    });
  }, [valid, cmp.nFlags, cmp.nMissing, result, mode]);

  // W3: emit the active employment type whenever it changes (auto or manual).
  const lastMode = useRef<string | null>(null);
  useEffect(() => {
    if (!valid) return;
    if (lastMode.current === mode) return;
    lastMode.current = mode;
    track("payslip_sollist_mode", { mode, auto: autoRef.current, is_example: !!result?.is_example });
  }, [valid, mode, result]);

  if (!valid) return null;

  const flaggedRows = cmp.rows.filter((row) => row.status !== "ok");

  return (
    <div>
      <SectionHeader
        title={<T de="Soll-Ist-Check: Zahlst du das Richtige?" en="Expected vs. actual: are you paying the right amounts?" />}
        hint={<T de="Abgleich mit dem 2026-Rechenwerk" en="Checked against the 2026 engine" />}
      />
      <Card variant="muted">
        <Stack gap="4">
          {/* Confirmable params */}
          <div>
            <span style={fieldLabel}><T de="Beschäftigungsart" en="Employment type" /></span>
            <div
              role="group"
              aria-label={tl(loc, "Beschäftigungsart", "Employment type")}
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: 6,
                margin: "0 0 12px",
              }}
            >
              {MODE_OPTS.map((m) => {
                const active = m.key === mode;
                return (
                  <button
                    key={m.key}
                    type="button"
                    onClick={() => {
                      if (m.key === mode) return;
                      autoRef.current = false;
                      setMode(m.key);
                    }}
                    aria-pressed={active}
                    style={{
                      padding: "6px 12px",
                      borderRadius: "var(--ck-radius-sm)",
                      border: active ? "1px solid var(--ck-primary)" : "1px solid var(--ck-hairline)",
                      background: active ? "var(--ck-primary)" : "transparent",
                      color: active ? "var(--ck-on-primary, #fff)" : "var(--ck-ink)",
                      fontFamily: "var(--ck-font-sans)",
                      fontSize: "var(--ck-text-sm)",
                      fontWeight: 600,
                      cursor: "pointer",
                    }}
                  >
                    {tl(loc, m.de, m.en)}
                  </button>
                );
              })}
            </div>
            <p style={{ fontSize: "var(--ck-text-sm)", color: "var(--ck-ink-muted)", margin: "0 0 8px" }}>
              <T
                de="Aus deiner Abrechnung übernommen — kurz prüfen, dann stimmt der Vergleich:"
                en="Prefilled from your payslip — confirm quickly so the comparison is right:"
              />
            </p>
            <div style={paramsGrid}>
              <div>
                <span style={fieldLabel}><T de="Steuerklasse" en="Tax class" /></span>
                <select
                  style={fieldCtl}
                  value={sk}
                  onChange={(e) => setSk(Number(e.target.value))}
                  aria-label={tl(loc, "Steuerklasse", "Tax class")}
                >
                  <option value={1}>{tl(loc, "I — ledig", "I — single")}</option>
                  <option value={2}>{tl(loc, "II — alleinerziehend", "II — single parent")}</option>
                  <option value={3}>{tl(loc, "III — verheiratet, höher", "III — married, higher")}</option>
                  <option value={4}>{tl(loc, "IV — verheiratet, gleich", "IV — married, equal")}</option>
                  <option value={5}>{tl(loc, "V — verheiratet, geringer", "V — married, lower")}</option>
                  <option value={6}>{tl(loc, "VI — Zweitjob", "VI — second job")}</option>
                </select>
              </div>
              <div>
                <span style={fieldLabel}>
                  <T de="KV-Zusatzbeitrag (%) · Ø 2,9" en="Health add-on rate (%) · avg 2.9" />
                </span>
                <input
                  style={fieldCtl}
                  type="number"
                  min={0}
                  max={6}
                  step={0.1}
                  value={kvz}
                  onChange={(e) => setKvz(Math.max(0, Math.min(6, Number(e.target.value) || 0)))}
                  aria-label={tl(loc, "Zusatzbeitrag deiner Krankenkasse in Prozent", "Your health insurer's additional rate in percent")}
                />
              </div>
              <div>
                <span style={fieldLabel}><T de="Bundesland (Kirchensteuer)" en="State (church tax)" /></span>
                <select
                  style={fieldCtl}
                  value={bl}
                  onChange={(e) => setBl(e.target.value)}
                  aria-label={tl(loc, "Bundesland", "Federal state")}
                >
                  {BL_OPTS.map((b) => (
                    <option key={b.code} value={b.code}>{b.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <span style={fieldLabel}><T de="Kinder" en="Kids" /></span>
                <select
                  style={fieldCtl}
                  value={kids}
                  onChange={(e) => setKids(Number(e.target.value))}
                  aria-label={tl(loc, "Anzahl Kinder", "Number of kids")}
                >
                  <option value={0}>{tl(loc, "keine", "none")}</option>
                  <option value={1}>1</option>
                  <option value={2}>2</option>
                  <option value={3}>3+</option>
                </select>
              </div>
            </div>
          </div>

          {/* SOLL vs IST table */}
          <div>
            <div style={{ ...rowGrid, padding: "0 0 6px" }}>
              <span style={colHead}><T de="Position" en="Line" /></span>
              <span style={{ ...colHead, textAlign: "right" }}><T de="Soll (berechnet)" en="Expected" /></span>
              <span style={{ ...colHead, textAlign: "right" }}><T de="Ist (Abrechnung)" en="Actual" /></span>
              <span />
            </div>
            {cmp.rows.map((row) => {
              const isNet = row.key === "netto";
              const statusColor =
                row.status === "ok" ? "var(--ck-success)" : row.status === "flag" ? "var(--ck-destructive)" : "var(--ck-ink-muted)";
              return (
                <div key={row.key} style={{ ...rowGrid, borderTop: "1px solid var(--ck-hairline)" }}>
                  <span style={{ ...cellLabel, fontWeight: isNet ? 700 : 600 }}>
                    <T de={row.de} en={row.en} />
                  </span>
                  <span style={{ ...cellNum, fontWeight: isNet ? 700 : 400 }}>{eur0(row.soll)}</span>
                  <span style={{ ...cellNum, fontWeight: isNet ? 700 : 400 }}>
                    {row.ist === null ? "–" : eur2(row.ist)}
                    {row.status === "missing" && (
                      <span style={{ display: "block", fontSize: "var(--ck-text-xs)", color: "var(--ck-ink-muted)", fontWeight: 400 }}>
                        {tl(loc, "nicht gefunden", "not found")}
                      </span>
                    )}
                    {row.status !== "missing" && row.delta !== null && Math.abs(row.delta) >= 1 && (
                      <span
                        style={{
                          display: "block",
                          fontSize: "var(--ck-text-xs)",
                          fontWeight: 400,
                          color: row.status === "flag" ? "var(--ck-destructive)" : "var(--ck-ink-muted)",
                        }}
                      >
                        {row.delta > 0 ? "+" : "−"}{eur0(Math.abs(row.delta))}
                      </span>
                    )}
                  </span>
                  <span style={{ display: "inline-flex", justifyContent: "flex-end", color: statusColor }}>
                    <Icon icon={row.status === "ok" ? CheckCircle : row.status === "flag" ? AlertTriangle : Info} decorative />
                  </span>
                </div>
              );
            })}
          </div>

          {/* W3: PKV premium is the user's private line — informational, not flagged. */}
          {mode === "pkv" && (
            <Tint tone="brand">
              <div className={styles.inlineNote}>
                <Icon icon={Info} decorative />
                <span>
                  {cmp.pkvPremium > 0 ? (
                    <T
                      de={`Privat versichert: KV/PV rechnet das Rechenwerk mit 0 €, weil du deine Prämie (${eur2(cmp.pkvPremium)}) selbst zahlst — sie ist deine private Position und wird hier nicht als Abweichung gewertet. Verglichen werden nur Lohnsteuer, Renten- und Arbeitslosenversicherung sowie Netto.`}
                      en={`Privately insured: the engine sets KV/PV to €0 because you pay your premium (${eur2(cmp.pkvPremium)}) yourself — it is your private line and is not counted as a deviation here. Only wage tax, pension & unemployment insurance and net are compared.`}
                    />
                  ) : (
                    <T
                      de="Privat versichert: KV und PV rechnet das Rechenwerk mit 0 €, weil du deine PKV-Prämie selbst zahlst. Diese private Position wird hier nicht bewertet — verglichen werden nur Lohnsteuer, Renten- und Arbeitslosenversicherung sowie Netto."
                      en="Privately insured: the engine sets KV and PV to €0 because you pay your PKV premium yourself. This private line is not evaluated here — only wage tax, pension & unemployment insurance and net are compared."
                    />
                  )}
                </span>
              </div>
            </Tint>
          )}

          {/* Verdict */}
          {cmp.nFlags === 0 && cmp.nMissing === 0 && (
            <Tint tone="green">
              <div className={styles.inlineNote}>
                <Icon icon={CheckCircle} decorative />
                <span>
                  <b><T de="Deine Abrechnung stimmt — auf den Euro." en="Your payslip checks out — to the euro." /></b>{" "}
                  <T
                    de="Alle Positionen liegen innerhalb der Toleranz (±2 % bzw. ±5 €)."
                    en="Every line is within tolerance (±2% / ±5€)."
                  />
                </span>
              </div>
            </Tint>
          )}
          {cmp.nFlags === 0 && cmp.nMissing > 0 && (
            <Tint tone="green">
              <div className={styles.inlineNote}>
                <Icon icon={CheckCircle} decorative />
                <span>
                  <b><T de="Keine Abweichungen in den erkannten Positionen." en="No deviations in the recognised lines." /></b>{" "}
                  <T
                    de="Ein paar Zeilen konnten wir nicht zuordnen — Hinweise unten."
                    en="A few lines could not be matched — notes below."
                  />
                </span>
              </div>
            </Tint>
          )}
          {cmp.nFlags > 0 && (
            <div className={styles.inlineNote}>
              <span style={{ color: "var(--ck-destructive-ink)", display: "inline-flex" }}>
                <Icon icon={AlertTriangle} decorative />
              </span>
              <span>
                {tl(
                  loc,
                  cmp.nFlags === 1
                    ? "1 Position weicht spürbar ab — unten steht, woran das meistens liegt und was du tun kannst."
                    : `${cmp.nFlags} Positionen weichen spürbar ab — unten steht, woran das meistens liegt und was du tun kannst.`,
                  cmp.nFlags === 1
                    ? "1 line deviates noticeably — below is what usually causes it and what to do."
                    : `${cmp.nFlags} lines deviate noticeably — below is what usually causes it and what to do.`,
                )}
              </span>
            </div>
          )}

          {/* Per-flag explanations */}
          {flaggedRows.length > 0 && (
            <Stack gap="3">
              {flaggedRows.map((row) => {
                if (row.status === "missing") {
                  return (
                    <Tint tone="warn" key={row.key}>
                      <p className={styles.flagTitle}>
                        {tl(loc, `${row.de}: auf der Abrechnung nicht gefunden`, `${row.en}: not found on the payslip`)}
                      </p>
                      <p className={styles.flagDesc}>
                        {tl(
                          loc,
                          `Erwartet wären ca. ${eur0(row.soll)}. Entweder wurde die Zeile nicht erkannt — oder sie fehlt wirklich. Für sozialversicherungspflichtige Angestellte ist diese Position Pflicht.`,
                          `Around ${eur0(row.soll)} would be expected. Either the line was not recognised — or it is genuinely missing. For regular employees this line is mandatory.`,
                        )}
                      </p>
                      <p className={styles.flagDesc}>
                        <b>{tl(loc, "Was tun:", "What to do:")}</b>{" "}
                        {tl(
                          loc,
                          "Sieh auf der Abrechnung nach, ob die Zeile dort steht. Fehlt sie komplett, kläre das direkt mit der Personalabteilung.",
                          "Check whether the line appears on the payslip. If it is missing entirely, clarify it with payroll right away.",
                        )}
                      </p>
                    </Tint>
                  );
                }
                const higher = (row.delta || 0) > 0;
                const c = mode === "minijob" ? minijobFlagCopy(row.key) : flagCopy(row.key, higher, { kvz, bl });
                return (
                  <Tint tone="warn" key={row.key}>
                    <p className={styles.flagTitle}>
                      {tl(
                        loc,
                        `${row.de}: ${eur0(Math.abs(row.delta || 0))} ${higher ? "mehr" : "weniger"} als erwartet${row.pct !== null ? ` (${row.pct} %)` : ""}`,
                        `${row.en}: ${eur0(Math.abs(row.delta || 0))} ${higher ? "more" : "less"} than expected${row.pct !== null ? ` (${row.pct}%)` : ""}`,
                      )}
                    </p>
                    <p className={styles.flagDesc}>{tl(loc, c.causeDe, c.causeEn)}</p>
                    <p className={styles.flagDesc}>
                      <b>{tl(loc, "Was tun:", "What to do:")}</b> {tl(loc, c.actDe, c.actEn)}
                    </p>
                  </Tint>
                );
              })}
            </Stack>
          )}

          {/* Assumptions + honest caveat */}
          <div style={{ fontSize: "var(--ck-text-xs)", color: "var(--ck-ink-muted)", lineHeight: 1.5 }}>
            <p style={{ margin: 0 }}>
              {mode === "minijob"
                ? tl(
                    loc,
                    "Annahmen: Minijob (bis 603 €) — voll ausgezahltes Brutto, keine Lohnsteuer und keine Arbeitnehmer-Sozialabgaben (der Arbeitgeber zahlt Pauschalabgaben). Ein möglicher RV-Eigenanteil von 3,6 % bleibt hier außen vor.",
                    "Assumptions: minijob (up to €603) — full gross paid out, no wage tax and no employee social contributions (the employer pays flat-rate contributions). A possible 3.6% pension employee share is not modelled here.",
                  )
                : mode === "werk"
                ? tl(
                    loc,
                    `Annahmen: Werkstudent:in · Steuerklasse ${roman(sk)} · nur Rentenversicherung (KV/PV/AV entfallen im Werkstudenten-Privileg) · Tarif 2026.`,
                    `Assumptions: working student · tax class ${roman(sk)} · pension insurance only (KV/PV/AV waived under the working-student privilege) · 2026 tables.`,
                  )
                : mode === "pkv"
                ? tl(
                    loc,
                    `Annahmen: Steuerklasse ${roman(sk)} · ${kids === 0 ? "kinderlos" : `${kids} Kind${kids > 1 ? "er" : ""}`} · privat kranken-/pflegeversichert (KV/PV im Soll = 0, Prämie zahlst du selbst) · ${kircheIst > 0 ? `Kirchensteuer ${cmp.churchPct} %` : "keine Kirchensteuer"} · Tarif 2026.`,
                    `Assumptions: tax class ${roman(sk)} · ${kids === 0 ? "no kids" : `${kids} kid${kids > 1 ? "s" : ""}`} · private health/care insurance (KV/PV expected = 0, you pay the premium) · ${kircheIst > 0 ? `church tax ${cmp.churchPct}%` : "no church tax"} · 2026 tables.`,
                  )
                : tl(
                    loc,
                    `Annahmen: Steuerklasse ${roman(sk)} · ${kids === 0 ? "kinderlos" : `${kids} Kind${kids > 1 ? "er" : ""}`} · KV-Zusatzbeitrag ${kvz.toLocaleString("de-DE")} % · ${kircheIst > 0 ? `Kirchensteuer ${cmp.churchPct} %` : "keine Kirchensteuer"} · gesetzlich versichert · Tarif 2026.`,
                    `Assumptions: tax class ${roman(sk)} · ${kids === 0 ? "no kids" : `${kids} kid${kids > 1 ? "s" : ""}`} · health add-on ${kvz.toLocaleString("de-DE")}% · ${kircheIst > 0 ? `church tax ${cmp.churchPct}%` : "no church tax"} · statutory insurance · 2026 tables.`,
                  )}
            </p>
            <p style={{ margin: "4px 0 0" }}>
              {tl(
                loc,
                "Ehrlicher Hinweis: Soll ist eine vereinfachte Jahresrechnung ÷ 12. Eine echte Monatsabrechnung (Lohnsteuertabelle, Einmalzahlungen, Rundungen) weicht davon oft ein paar Euro ab — kleine Differenzen sind normal und kein Fehler.",
                "Honest note: the expected values are a simplified annual calculation ÷ 12. A real monthly payslip (wage-tax table, one-off payments, rounding) often differs by a few euros — small gaps are normal, not an error.",
              )}
              {bl === "SN"
                ? tl(
                    loc,
                    " In Sachsen liegt dein Pflege-Anteil zudem 0,5 Punkte über dem hier angesetzten Wert.",
                    " In Saxony your care-insurance share is additionally 0.5 points above the value assumed here.",
                  )
                : ""}
            </p>
          </div>
        </Stack>
      </Card>
    </div>
  );
}
